import ReactDOM from 'react-dom';
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  ReactNode,
  useContext,
} from 'react';
import styled from 'astroturf/react';
import {
  autoUpdate,
  computePosition,
  flip,
  shift,
  offset,
  size,
} from '@floating-ui/react';
import { Spinner } from 'js/academia_components/transients/spinner';
import { scrollChildIntoView } from '../utils';
import { mod } from 'js/social/shared/SocialEditor/utils';
import { Body } from 'js/design_system/Body';
import { ModalElementContext } from 'js/design_system/Modal';
import _Popup from "Autocomplete-Popup.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/SocialEditor/Autocomplete.tsx?styleId=Popup";
import _Message from "Autocomplete-Message.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/SocialEditor/Autocomplete.tsx?styleId=Message";
import _List from "Autocomplete-List.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/SocialEditor/Autocomplete.tsx?styleId=List";
import _ListItem from "Autocomplete-ListItem.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/social/shared/SocialEditor/Autocomplete.tsx?styleId=ListItem";


const MAX_HEIGHT = 400;

const Popup = /*#__PURE__*/styled("div", null, {
  displayName: "Popup",
  styles: _Popup
});

const Message = /*#__PURE__*/styled(Body, null, {
  displayName: "Message",
  styles: _Message
});

const List = /*#__PURE__*/styled("div", null, {
  displayName: "List",
  styles: _List
});

const ListItem = /*#__PURE__*/styled("div", null, {
  displayName: "ListItem",
  styles: _ListItem
});

type Props<T> = {
  anchorTop: number;
  anchorLeft: number;
  items: T[];
  message?: string;
  activeIndex: number;
  loading: boolean;
  keyExtractor: (item: T) => React.Key;
  renderItem: (item: T) => ReactNode;
  onActiveIndexChange: (activeIndex: number) => void;
  onSelect: (item: T) => void;
};

export const Autocomplete = <T,>({
  anchorTop,
  anchorLeft,
  items = [],
  message,
  activeIndex,
  loading,
  keyExtractor,
  renderItem,
  onActiveIndexChange,
  onSelect,
}: Props<T>) => {
  const [activeDiv, setActiveDiv] = useState<HTMLDivElement | null>(null);
  const [popoverDiv, setPopoverDiv] = useState<HTMLDivElement | null>(null);
  const portalContainer = useContext(ModalElementContext)?.current;

  const anchorElement = useMemo(() => {
    return {
      getBoundingClientRect: () => ({
        width: 0,
        height: 14, // Account for line height
        x: anchorLeft,
        y: anchorTop,
        left: anchorLeft,
        right: anchorLeft,
        top: anchorTop,
        bottom: anchorTop,
      }),
    };
  }, [anchorLeft, anchorTop]);

  const updatePosition = useCallback(() => {
    if (!popoverDiv) return;

    computePosition(anchorElement, popoverDiv, {
      placement: 'bottom-start',
      middleware: [
        flip({ mainAxis: false }),
        shift(),
        size({
          apply({ availableWidth, availableHeight, elements }) {
            Object.assign(elements.floating.style, {
              maxWidth: `${availableWidth}px`,
              maxHeight: `${Math.min(availableHeight, MAX_HEIGHT)}px`,
            });
          },
        }),
        offset(4),
      ],
    }).then(({ x, y }) => {
      Object.assign(popoverDiv.style, {
        top: `${y}px`,
        left: `${x}px`,
      });
    });
  }, [anchorElement, popoverDiv]);

  useEffect(() => {
    if (popoverDiv) {
      return autoUpdate(anchorElement, popoverDiv, updatePosition);
    }
  }, [anchorElement, popoverDiv, updatePosition]);

  useEffect(() => {
    scrollChildIntoView(popoverDiv, activeDiv);
  }, [popoverDiv, activeDiv]);

  return ReactDOM.createPortal(
    <Popup ref={(ref) => setPopoverDiv(ref)}>
      {loading ? (
        <Spinner show />
      ) : message ? (
        <Message size="md">{message}</Message>
      ) : (
        <List>
          {items.map((item, index) => {
            const active = mod(activeIndex, items.length) === index;

            return (
              <ListItem
                ref={(ref) => active && setActiveDiv(ref)}
                key={keyExtractor(item)}
                active={active}
                onMouseEnter={() => onActiveIndexChange(index)}
                onClick={() => onSelect(item)}
              >
                {renderItem(item)}
              </ListItem>
            );
          })}
        </List>
      )}
    </Popup>,
    portalContainer ?? document.body,
  );
};
